import React, { useState, useEffect } from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import {
  Link,
  navigate,
  RouteComponentProps,
  useLocation,
  WindowLocation,
} from '@gatsbyjs/reach-router';
import { Auth } from '~/amplify';
import { Body, Heading1 } from '~/components/atom/Typography';
import { Form, Field } from '~/components/organism/Forms';
import OTPInput from '~/components/molecule/OTPInput';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import AuthFormWrapper from '../components/AuthFormWrapper';
import createPageTitle from '~/util/createPageTitle';
import styled, { css } from 'styled-components';
import { reporter } from '~/hooks/useErrorReporter';
import Loading from '~/components/atom/Loading';
import Catalog from '~/Catalog';
import { PROFILE_PATH } from '../../Settings/Profile/constants';
import { AUTH_PATHS } from '../constants';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import TOTPSetupInstructions from '~/components/atom/TOTPSetupInstructions';
import InfoBlock from '~/components/molecule/InfoBlock';
import useGetCurrentAccount from '../../AccountSelectionWrapper/hooks/useGetCurrentAccount';
import useUpdateMFAPreferenceAndSyncUser from '~/amplify/utils/updateMFAPreferenceAndSyncUser';
import { buildTotpOtpAuthUrl } from '~/util/buildTotpOtpAuthUrl';

const text = {
  title: 'Tweestaps\u00ADverificatie instellen',
  submitButton: 'Code verifiëren',
  errorMessage: 'Ongeldige verificatiecode. Probeer het opnieuw.',
  successMessage: 'Tweestapsverificatie succesvol ingesteld',
  noUserError: 'Tweestapsverificatie instellen mislukt. Probeer het opnieuw.',
  noQRCode:
    'Geen QR-code gevonden om tweestapsverificatie in te stellen voor deze gebruiker.',
  goBackToLogin: 'Terug naar inlogpagina',
  code: 'Code',
  noAccountError:
    'Geen account gevonden om tweestapsverificatie in te stellen.',
  impersonatedErrorMessage:
    'Je kunt tweestapsverificatie niet instellen terwijl je een andere gebruiker imiteert.',
  adminNeedsToSetupMFA:
    'Je moet tweestapsverificatie instellen voor jezelf voordat je een andere gebruiker imiteert.',
  goToProfile: 'Ga naar je profiel',
};

type WindowState = {
  isAboutToImpersonate?: boolean;
  message?: string;
};

type Props = RouteComponentProps & {
  accountId: string;
};

const MFASetupTOTP: React.FC<Props> = ({ accountId }) => {
  const location = useLocation() as WindowLocation<WindowState>;
  const [error, setError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [userName, setUserName] = useState<string | null>(null);

  const { updateMFAPreferenceWithSync, loading: updateLoading } =
    useUpdateMFAPreferenceAndSyncUser();

  useEffect(() => {
    const setupMFA = async () => {
      try {
        // Check if MFA is already enabled
        const mfaPreferences = await Auth.fetchMFAPreference();
        const isTotpAlreadyEnabled =
          mfaPreferences.enabled &&
          mfaPreferences.enabled.length > 0 &&
          mfaPreferences.enabled.includes('TOTP');

        if (isTotpAlreadyEnabled) {
          void navigate(PROFILE_PATH, { replace: true });
          return;
        }

        // Prepare MFA setup
        const currentUser = await Auth.getCurrentUser();
        const attributes = await Auth.fetchUserAttributes();
        setUserName(attributes?.name || null);

        const result = await Auth.setUpTOTP();
        const cognitoId = currentUser.username;

        setQrCode(
          buildTotpOtpAuthUrl({
            accountIdentifier: cognitoId,
            sharedSecret: result.sharedSecret,
          }),
        );

        setPageLoading(false);
      } catch (err) {
        setPageLoading(false);
        reporter.captureMessage(`MFA: Failed to setUpTOTP: ${err}`, 'error');
        setError(text.noUserError);
      }
    };

    void setupMFA();
  }, [accountId]);

  const onFormSubmit = async ({
    code,
    accountId,
  }: {
    code: string;
    accountId: string;
  }) => {
    setError(null);

    try {
      await Auth.verifyTOTPSetup({ code });

      await updateMFAPreferenceWithSync({
        accountId,
        update: { totp: 'ENABLED' },
      });

      const [attributes, mfaPreferences] = await Promise.all([
        Auth.fetchUserAttributes(),
        Auth.fetchMFAPreference(),
      ]);
      const hasVerifiedPhone =
        attributes.phone_number != null &&
        attributes.phone_number_verified === 'true';
      const smsEnabled = mfaPreferences.enabled?.includes('SMS') ?? false;

      if (hasVerifiedPhone && smsEnabled) {
        void navigate('/-/', { replace: true });
        return;
      }

      void navigate(AUTH_PATHS.MFA_SETUP_PHONE, { replace: true });
      return;
    } catch (err) {
      if (isExpiredOrNotAuthorizedSessionError(err)) {
        void navigate(AUTH_PATHS.LOGIN, {
          replace: true,
          state: { message: Catalog.sessionExpired },
        });
        return;
      }
      reporter.captureMessage(
        `MFA: Failed to verifyTOTPSetup: ${err}`,
        'error',
      );
      setError(text.errorMessage);
    }
  };

  if (pageLoading) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
        </MetaTags>
        <Loading />
      </>
    );
  }

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>
      <AuthFormWrapper>
        <HeaderContainer>
          <Heading1 lineHeight="base" color={{ group: 'accent' }} size="xxxl">
            {text.title}
          </Heading1>
          {userName && (
            <Heading1
              lineHeight="base"
              color={{ group: 'accent' }}
              size="m"
              fontWeight="medium"
            >
              voor {userName}
            </Heading1>
          )}
        </HeaderContainer>

        {location.state?.message && (
          <InfoBlock
            appearance="info"
            padding={['base']}
            inline
            icon={{
              name: 'info',
              backgroundColor: { group: 'info' },
              color: { group: 'white' },
            }}
            headerText={null}
            margin={[null, null, 'xl']}
          >
            {location.state.message}
          </InfoBlock>
        )}

        {qrCode ? (
          <Form
            initialValues={{ code: '' }}
            onSubmit={({ code }) => onFormSubmit({ code, accountId })}
          >
            {({ handleSubmit, submitting, form }) => (
              <StyledForm onSubmit={handleSubmit}>
                <TOTPSetupInstructions qrCode={qrCode} onAuthPage />

                <Field name="code">
                  {({ input }) => (
                    <OTPInput
                      length={6}
                      value={input.value ?? ''}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      disabled={submitting}
                      autoFocus
                      onComplete={() => {
                        void form.submit();
                      }}
                    />
                  )}
                </Field>

                {error && (
                  <Body
                    margin={[null]}
                    color={{ group: 'danger', variant: 'light' }}
                    size="base"
                  >
                    {error}
                  </Body>
                )}

                <Button
                  appearance="primary"
                  size="medium"
                  type="submit"
                  disabled={submitting}
                  loading={submitting || updateLoading}
                  label={text.submitButton}
                />
              </StyledForm>
            )}
          </Form>
        ) : (
          <JustificationContainer
            direction="column"
            gap="xl"
            align="center"
            padding={['l']}
          >
            <Body
              margin={[null]}
              color={{ group: 'white' }}
              size="base"
              align="center"
            >
              {text.noQRCode}
            </Body>
            <TextButton
              appearance="secondary"
              size="medium"
              onClick={() => void navigate(AUTH_PATHS.LOGIN, { replace: true })}
              label={text.goBackToLogin}
              icon="arrowLeft"
              dataTestId="mfa-setup-totp-go-back-to-login-button"
            />
          </JustificationContainer>
        )}
      </AuthFormWrapper>
    </>
  );
};

const MFASetupTOTPWrapper: React.FC<RouteComponentProps> = () => {
  const location = useLocation() as WindowLocation<WindowState>;
  const result = useGetCurrentAccount();
  const accountId = result?.accountId ?? null;
  const loading = result?.loading ?? false;

  if (loading || result?.loading) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
        </MetaTags>
        <Loading />
      </>
    );
  }

  if (!accountId) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
        </MetaTags>
        <Body>{text.noAccountError}</Body>
      </>
    );
  }

  // User is about to be impersonated, but impersonator does not have MFA setup
  if (location.state?.isAboutToImpersonate) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
        </MetaTags>
        <AuthFormWrapper>
          <Body color={{ group: 'white' }}>
            {text.adminNeedsToSetupMFA}{' '}
            <Link to={PROFILE_PATH}>{text.goToProfile}</Link>
          </Body>
        </AuthFormWrapper>
      </>
    );
  }

  return <MFASetupTOTP accountId={accountId} />;
};

const StyledForm = styled.form(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.space('xl')};
  `,
);

const HeaderContainer = styled.div(
  ({ theme }) => css`
    width: 100%;
    padding-left: ${theme.space('xl')};
    margin-bottom: ${theme.space('xxl')};
  `,
);

export default MFASetupTOTPWrapper;
